import { Component, OnInit, ViewChild } from '@angular/core';
import * as XLSX from 'xlsx';
import { MediaService } from 'src/app/services/media.service';
import { SettingsService } from 'src/app/services/settings.service';
import { TransactionService } from 'src/app/services/transaction.service';
import { Transaction } from 'src/app/shared/models/transactions/transaction';
import { TransactionSearchRequest } from 'src/app/shared/models/transactions/transaction-search-request';
import { formatDate } from '@angular/common';
import { TransactionStatus } from 'src/app/shared/models/transactions/transaction-status';
import { HttpErrorResponse } from '@angular/common/http';
import { TransactionDealService } from 'src/app/services/transaction-deal.service';
import { TransactionDeals } from 'src/app/shared/models/transaction-deals/transaction-deal';
import { LookupService } from 'src/app/services/lookup.service';
import { TransactionStatusUtil } from 'src/app/utils/transaction-status-util';
import { TradeMediaInfo } from 'src/app/shared/models/general/trade-media-info';
import { DatePipe } from '@angular/common';
import { BadTransactionCount } from 'src/app/shared/models/transactions/bad-transaction-count';
import { SystemService } from 'src/app/services/system.service';

@Component({
  selector: 'app-current-trades',
  templateUrl: './current-trades.component.html',
  styleUrls: ['./current-trades.component.scss'],
})
export class CurrentTradesComponent implements OnInit {
  @ViewChild('userFiaPinModal', { static: true }) userFiaPinModal: any;

  busy: boolean = true;
  downloadBusy: boolean = false;
  modalBusy: boolean = true;
  showError: boolean = false;
  showModalError: boolean = false;
  errorMsg: string = 'Oops! Looks like something went wrong, please contact support if this issue persists';
  errorResponse: HttpErrorResponse;

  lastMessage: string;

  transactionSearchRequest: TransactionSearchRequest = new TransactionSearchRequest();
  transactions: Transaction[] = [];
  selectedTransaction: Transaction = new Transaction();
  selectedTransactionDeal: TransactionDeals = new TransactionDeals();
  selectedTransactionStatus: TransactionStatus = new TransactionStatus();

  showTradeEditModal: boolean = false;

  transactionStatuses: TransactionStatus[] = [];
  filterStatuses: TransactionStatus[] = [];

  updatedDate: Date;
  dealRate: number = 0;
  roleId: number = 0;

  constructor(
    public settingsService: SettingsService,
    public transactionService: TransactionService,
    public dealService: TransactionDealService,
    public mediaService: MediaService,
    private lookupService: LookupService,
    private datePipe: DatePipe,
    private systemService: SystemService
  ) {}

  setVisualStatusClass(trade: Transaction) {
    return {
      'update-success': trade.recentlyUpdated,
      'update-fail': trade.error,
      critical: trade.isCritical || TransactionStatus.criticalStatuses.includes(trade.transactionStatusId),
      emergency: TransactionStatus.emergencyStatuses.includes(trade.transactionStatusId),
      semiUrgent: TransactionStatus.semiUrgentStatuses.includes(trade.transactionStatusId),
      normal: !trade.isCritical && TransactionStatus.isNormalStatus(trade.transactionStatusId),
      authorised: trade.transactionStatusId === 241,
    };
  }

  private checkForCriticalAndHandleDelay(trade: Transaction) {
    const foundIndex = this.transactions.findIndex((x) => x.id == trade.id);
    let originalTrade = this.transactions[foundIndex];

    if (!originalTrade) {
      return;
    }

    if (originalTrade.transactionStatusId != trade.transactionStatusId) {
      originalTrade.transactionStatusId = trade.transactionStatusId;
      originalTrade.isCritical = false;
      return;
    }

    if (this.isTradeInAttemptingStatus(trade) == false) {
      return;
    }

    if (this.IsTradeTooOld(trade)) {
      originalTrade.isCritical = true;
      return;
    }

    setTimeout(() => {
      this.GetTradeAndCheckForCriticalStatus(trade.id);
    }, 1000 * 30);
  }

  private isTradeInAttemptingStatus(trade: Transaction) {
    const attemptingToBookDealStatus = 228;
    if (trade.transactionStatusId != attemptingToBookDealStatus) {
      return false;
    }

    return true;
  }

  private IsTradeTooOld(trade: Transaction) {
    if (this.dealsExist(trade)) {
      if (this.getDifferenceInMilliseconds(trade) > 1000 * 60) {
        return true;
      }
    }

    return false;
  }

  private getDifferenceInMilliseconds(trade: Transaction): number {
    const deal = trade.transactionDeals[0];
    const lastUpdatedDate = new Date(deal.lastUpdated);
    const currentDate = new Date();

    const differenceInMilliseconds = currentDate.getTime() - lastUpdatedDate.getTime();

    return differenceInMilliseconds;
  }

  public dealHasLastMessage(trade: Transaction) {
    if (TransactionStatus.isNormalStatus(trade.transactionStatusId)) {
      return false;
    }

    if (TransactionStatus.isNoneStatus(trade.transactionStatusId)) {
      return false;
    }
    if (this.dealsExist(trade)) {
      const deal = trade.transactionDeals[0];
      if (deal.lastMessage) {
        this.lastMessage = deal.lastMessage;
        return true;
      }
    }

    this.lastMessage = '';
    return false;
  }

  private dealsExist(trade: Transaction): boolean {
    const deals = trade?.transactionDeals;
    if (!deals?.length) {
      return false;
    }
    return true;
  }

  private getDealRetryCount(trade: Transaction): number {
    if (this.dealsExist(trade)) {
      return trade.transactionDeals[0].retryCount;
    }
    return -1;
  }

  public buildTradeInfoText(trade: Transaction): string {
    let info = `Id: ${trade.id}`;
    const retryCount = this.getDealRetryCount(trade);

    if (retryCount > -1) {
      info += ` | Retries: ${retryCount}`;
    }
    const startDate = this.datePipe.transform(trade.transactionStartDate, 'dd MMM - hh:mm');
    info += ` | User: ${trade.userId} | ${startDate}`;

    return info;
  }

  formatDateWithDayCheck(date: Date | string | null): string {
    if (!date) return '';

    const dateObj = new Date(date);
    const today = new Date();

    // Reset time to midnight for comparison
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const dateMidnight = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());

    // Check if date is from today
    if (dateMidnight.getTime() === todayMidnight.getTime()) {
      return this.datePipe.transform(dateObj, 'HH:mm') || '';
    }

    // If not today, show day of week and time
    return this.datePipe.transform(dateObj, 'EEE HH:mm') || '';
  }

  ngOnInit(): void {
    this.busy = true;
    this.GetTransactionStatuses();
  }

  private loadSettings() {
    this.systemService.loadPollUntilAuthorised().subscribe(() => {
      this.finishLoadSettings();
    });
  }

  private finishLoadSettings() {
    this.settingsService.ValidateGlobalVars().then(() => {
      this.SetupUserTradingSearchRequest();
      this.SearchTrades();
      this.roleId = this.settingsService.userRoles[0].roleId;
    });
  }

  private SetupUserTradingSearchRequest() {
    this.transactionSearchRequest.brokerId = this.settingsService.brokerUsers[0].brokerId;
    this.transactionSearchRequest.todaysTradesOnly = true;
    this.transactionSearchRequest.transactionStatusId = this.filterStatuses[1].id;
    this.transactionSearchRequest.includeDocuments = false;
  }

  SearchTrades() {
    this.GetBadTransactionCounts();
    this.busy = true;
    this.transactionService.SearchTransactions(this.transactionSearchRequest).subscribe({
      next: (response) => {
        this.transactions = response;
        this.transactions.forEach((trade) => {
          this.checkForCriticalAndHandleDelay(trade);
        });
        this.busy = false;
        this.showError = false;
      },
      error: (error) => {
        this.showError = true;
        this.errorMsg = error.error.ErrorDetail;
        this.busy = false;
      },
    });
  }

  badStatusCounts: BadTransactionCount[] = [];
  badStatusListToShow: number[] = [20, 50, 60, 90, ...TransactionStatus.emergencyStatuses, ...TransactionStatus.criticalStatuses];

  GetBadTransactionCounts() {
    this.transactionService.FindBadTransactions().subscribe({
      next: (response) => {
        this.badStatusCounts = response?.filter((x) => this.badStatusListToShow.includes(x.statusId)) || [];
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  getStatusName(statusId: number): string {
    const status = TransactionStatusUtil.FindById(this.transactionStatuses, statusId);

    return `${status.name}`;
  }

  getBadgeColorForStatus(statusId: number): string {
    if (TransactionStatus.emergencyStatuses.includes(statusId)) {
      return 'red';
    }

    if (TransactionStatus.criticalStatuses.includes(statusId)) {
      return 'red';
    }

    return 'amber darken-2';
  }

  GetTradeAndCheckForCriticalStatus(id: number) {
    console.log('fetching new status for trade ' + id);
    this.transactionService.GetTransactionById(id).subscribe({
      next: (trade: Transaction) => {
        this.checkForCriticalAndHandleDelay(trade);
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  DownloadFile(id: number) {
    this.busy = true;
    this.mediaService.DownloadMedia(id).subscribe({
      next: (response) => {
        this.mediaService.Download(response);
        this.busy = false;
      },
      error: (error) => {
        this.showError = true;
        this.errorMsg = error.error.ErrorDetail;
        this.busy = false;
      },
    });
  }

  DownloadFiles(allFiles: boolean = false) {
    this.downloadBusy = true;

    var selectedTransactions: Transaction[] = [];
    if (allFiles) {
      selectedTransactions = this.transactions;
    } else {
      selectedTransactions = this.transactions.filter((x) => x.selected == true);
    }

    if (selectedTransactions.length == 0 && allFiles == false) {
      this.showError = true;
      this.errorMsg = 'Please select at least 1 trade';
      this.downloadBusy = false;
      return;
    }

    var tradeMediaInfo: TradeMediaInfo[] = [];
    selectedTransactions.forEach((element) => {
      var mediaInfo: TradeMediaInfo = {
        fullName: element.fullName,
        mediaIds: [],
      };
      element.supportingMedia.forEach((el) => {
        mediaInfo.mediaIds.push(el.id);
      });
      tradeMediaInfo.push(mediaInfo);
    });

    this.mediaService.DownloadMediaList(tradeMediaInfo).subscribe({
      next: (response) => {
        this.mediaService.Download(response);
        this.showError = false;
        this.downloadBusy = false;
      },
      error: (error) => {
        this.showError = true;
        this.errorMsg = error.error.ErrorDetail;
        this.downloadBusy = false;
      },
    });
  }

  onShowFiaPinsModal(transaction: Transaction) {
    this.selectedTransaction = transaction;
    this.userFiaPinModal.show();
  }

  sdaUsed(trade: Transaction): boolean {
    if (trade.sdaAmount > 0) {
      return true;
    }
    return false;
  }

  GetStatusDisplayName(statusId: number) {
    const status = TransactionStatusUtil.FindById(this.transactionStatuses, statusId);
    return status.name;
  }

  GetHoverHelpText(statusId: number) {
    const status = TransactionStatusUtil.FindById(this.transactionStatuses, statusId);
    return status.helpText;
  }

  IsTradeEditable(trade: Transaction) {
    const statusId = trade.transactionStatusId;
    const retryCount = this.getDealRetryCount(trade);

    if (statusId == 230 && retryCount < 6) {
      return false;
    }

    return TransactionStatus.canEditTradeList.includes(statusId);
  }

  EditTransactionAndDeal(transaction: Transaction) {
    this.busy = true;
    this.dealService.GetDealByTransactionId(transaction.id).subscribe({
      next: (deal: TransactionDeals) => {
        this.selectedTransactionDeal = deal;
        this.selectedTransaction = transaction;
        this.selectedTransactionStatus = TransactionStatusUtil.FindById(this.transactionStatuses, transaction.transactionStatusId);
        this.showTradeEditModal = true;
        this.busy = false;
      },
      error: (error) => {
        transaction.error = error;
        this.handleModalError(transaction);
        this.busy = false;
      },
    });
  }

  GetTransactionStatuses() {
    this.lookupService.GetTransactionStatuses().subscribe({
      next: (statusList: TransactionStatus[]) => {
        this.transactionStatuses = statusList;
        this.BuildFilterStatuses();
        this.loadSettings();
      },
      error: (error) => {
        this.errorResponse = error;
        this.showModalError = true;
        this.busy = false;
      },
    });
  }

  adminStatuses: { id: number; name: string; helpText: string }[] = [
    {
      id: -2,
      name: 'Prebooking',
      helpText: 'All trades in prebooking (Dooya portion)',
    },
    {
      id: -1,
      name: 'Book Now Received',
      helpText: 'All trades that have received a book now message',
    },
    {
      id: 0,
      name: 'Not Complete',
      helpText: 'Show all incomplete statuses',
    },
  ];

  BuildFilterStatuses() {
    if (this.roleId != 1) {
      this.filterStatuses = this.adminStatuses as TransactionStatus[];
      return;
    }
    this.filterStatuses = this.transactionStatuses;
  }

  Extract(idselector: string) {
    var filename = 'InProgressTransactions';
    let element = document.getElementById(idselector);
    var aoa: any[][] = [this.transactions, [0]];
    //aoa[0][0] = this.transactions ;
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);

    /* generate workbook and add the worksheet */
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    /* save to file */
    XLSX.writeFile(wb, filename + formatDate(new Date(), 'yyyyMMdd_HHmm', 'en') + '.xlsx');
  }

  updateOriginalTrade(editedTransaction: Transaction) {
    const foundIndex = this.transactions.findIndex((x) => x.id === editedTransaction.id);
    let original = this.transactions[foundIndex];

    original.transactionStatusId = editedTransaction.transactionStatusId;
    original = editedTransaction;

    this.selectedTransaction = original;
    this.selectedTransactionDeal = original.transactionDeals[0];

    original.updateSuccess = true;
    original.recentlyUpdated = true;
    setTimeout(() => {
      original.recentlyUpdated = false;
    }, 3000);
  }

  handleModalError(originalTransaction: Transaction) {
    this.errorResponse = originalTransaction.error;
    this.showModalError = true;
  }

  popupError(error: HttpErrorResponse) {
    //Note: the spread operator here ... creates a new instance of the error so that the onChanges can detect the change in the errorcomponent
    this.errorResponse = { ...error };
    this.showModalError = true;
  }
}
